import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  Skeleton,
  type ChartConfig,
} from '@tinct/web-components';
import { ReadAdsCampaign, ReadAdsCampaignMetric } from '@tinct/core-api-models';
import { useGetAdsCampaignMetrics } from '@tinct/core-api';
import { DateRangePicker, DateRangeValue, lastDays } from './date-range-picker';

type CampaignMetricsPanelProps = {
  integrationId: string;
  campaign: ReadAdsCampaign;
};

const CHART_CONFIG = {
  impressions: { label: 'Impressions', color: '#0A66C2' },
  clicks: { label: 'Clicks', color: '#F59E0B' },
} satisfies ChartConfig;

export type MetricsSummary = {
  impressions: number;
  clicks: number;
  ctr: number | null;
  spend: number;
  currency: string | null;
  cpc: number | null;
  conversions: number;
  leads: number;
};

/** Client-side aggregation of daily rows (the API returns one row per day). */
export function summarizeMetrics(rows: ReadAdsCampaignMetric[]): MetricsSummary {
  const sum = (pick: (row: ReadAdsCampaignMetric) => number | null | undefined) =>
    rows.reduce((total, row) => total + (pick(row) ?? 0), 0);
  const impressions = sum((r) => r.impressions);
  const clicks = sum((r) => r.clicks);
  const spend = sum((r) => r.costLocal);
  return {
    impressions,
    clicks,
    ctr: impressions > 0 ? clicks / impressions : null,
    spend,
    currency: rows.find((r) => r.currency)?.currency ?? null,
    cpc: clicks > 0 ? spend / clicks : null,
    conversions: sum((r) => r.conversions),
    leads: sum((r) => r.leads),
  };
}

const integer = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const percent = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const formatMoney = (amount: number, currency: string | null) => {
  if (!currency) return amount.toFixed(2);
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
};

const toApiDate = (date: Date) => format(date, 'yyyy-MM-dd');

function KpiTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-[120px] flex-1 flex-col gap-1 rounded-lg border border-border bg-background px-4 py-3">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-lg font-semibold text-foreground">{value}</span>
    </div>
  );
}

/**
 * Daily metrics of one synced campaign: a date window (default last 30
 * days), a KPI row computed client-side, and an impressions/clicks chart.
 */
export function CampaignMetricsPanel({ integrationId, campaign }: CampaignMetricsPanelProps) {
  const [range, setRange] = useState<DateRangeValue>(() => lastDays(30));

  const { data, isLoading, isError, error } = useGetAdsCampaignMetrics({
    integrationId,
    adsCampaignId: campaign.id ?? '',
    from: toApiDate(range.from),
    to: toApiDate(range.to),
    offset: 0,
    // One row per day: a year of history fits in a single page.
    limit: 400,
    sort: { field: 'metricDate', direction: 'asc' },
  });

  const rows = useMemo(() => data?.data ?? [], [data]);
  const summary = useMemo(() => summarizeMetrics(rows), [rows]);
  const chartData = useMemo(
    () =>
      rows
        .filter((r) => r.metricDate)
        .map((r) => ({
          date: r.metricDate as string,
          impressions: r.impressions ?? 0,
          clicks: r.clicks ?? 0,
        })),
    [rows],
  );

  const dateLabel = (value: string) =>
    new Date(`${value}T00:00:00Z`).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });

  return (
    <section
      className="flex flex-col gap-4 rounded-xl border border-border bg-card px-5 py-4"
      data-testid="linkedin-campaign-metrics"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <h3 className="text-sm font-medium text-foreground">
            {campaign.name ?? campaign.externalCampaignId ?? 'Campaign'} · daily metrics
          </h3>
          <p className="text-xs text-muted-foreground">
            Refreshed at every sync (the last 30 days are re-fetched — LinkedIn
            back-fills conversions for a few days).
          </p>
        </div>
        <DateRangePicker value={range} onChange={setRange} />
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-3">
          <div className="flex gap-3">
            {Array.from({ length: 7 }).map((_, i) => (
              <Skeleton key={i} className="h-16 flex-1" />
            ))}
          </div>
          <Skeleton className="h-[240px] w-full" />
        </div>
      ) : isError ? (
        <p className="text-sm text-destructive">
          {error?.error ?? 'Could not load the metrics.'}
        </p>
      ) : rows.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          No metrics in this window. They land with the campaign sync — test ad
          accounts never report metrics.
        </p>
      ) : (
        <>
          <div className="flex flex-wrap gap-3">
            <KpiTile label="Impressions" value={integer.format(summary.impressions)} />
            <KpiTile label="Clicks" value={integer.format(summary.clicks)} />
            <KpiTile label="CTR" value={summary.ctr === null ? '-' : percent.format(summary.ctr)} />
            <KpiTile label="Spend" value={formatMoney(summary.spend, summary.currency)} />
            <KpiTile label="CPC" value={summary.cpc === null ? '-' : formatMoney(summary.cpc, summary.currency)} />
            <KpiTile label="Conversions" value={integer.format(summary.conversions)} />
            <KpiTile label="Leads" value={integer.format(summary.leads)} />
          </div>

          <ChartContainer config={CHART_CONFIG} className="h-[240px] w-full">
            <LineChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 12 }}
                interval="preserveStartEnd"
                tickMargin={8}
                tickFormatter={dateLabel}
              />
              {/* Impressions dwarf clicks: two axes so both curves stay readable. */}
              <YAxis yAxisId="impressions" hide />
              <YAxis yAxisId="clicks" orientation="right" hide />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    labelFormatter={(value) => dateLabel(String(value))}
                    formatter={(value, name) => (
                      <span className="flex w-full justify-between gap-4">
                        <span className="text-muted-foreground">
                          {CHART_CONFIG[name as keyof typeof CHART_CONFIG]?.label ?? name}
                        </span>
                        <span className="font-medium">{integer.format(Number(value))}</span>
                      </span>
                    )}
                  />
                }
              />
              <Line
                yAxisId="impressions"
                type="monotone"
                dataKey="impressions"
                stroke={CHART_CONFIG.impressions.color}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
              <Line
                yAxisId="clicks"
                type="monotone"
                dataKey="clicks"
                stroke={CHART_CONFIG.clicks.color}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
              <ChartLegend content={<ChartLegendContent />} />
            </LineChart>
          </ChartContainer>
        </>
      )}
    </section>
  );
}
